'use client'

import { Store } from '@tanstack/react-store'

// --- Types ---

export interface PendingEdit {
  file: string
  id?: string
  type: 'update' | 'add' | 'delete' | 'reorder'
  /**
   * For 'update'/'add': item field values.
   * For 'delete': empty.
   * For 'reorder': `{ orderedIds: string[] }` — the full ordered id list for the file.
   */
  data: Record<string, unknown>
}

const REORDER_KEY_SUFFIX = '__reorder__'

export interface CmsStoreState {
  /** Keyed by "file" or "file:id" */
  pendingEdits: Record<string, PendingEdit>
  /** Pending image uploads: base64 data URLs keyed by intended path */
  pendingImages: Record<string, string>
  /** True after edits have been published but before the redeploy lands */
  published: boolean
}

// --- Helpers ---

function editKey(file: string, id?: string) {
  return id ? `${file}:${id}` : file
}

const STORAGE_KEY = 'msb-cms-pending'

const EMPTY_STATE: CmsStoreState = { pendingEdits: {}, pendingImages: {}, published: false }

function saveToStorage(state: CmsStoreState) {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // quota exceeded — ignore
  }
}

// --- Store ---

// Initialize empty on both server and client so the first client render matches SSR.
// `hydrateFromStorage` is called once after mount to restore pending edits.
export const cmsStore = new Store<CmsStoreState>(EMPTY_STATE)

let hydrated = false
export function hydrateFromStorage() {
  if (hydrated || typeof window === 'undefined') return
  hydrated = true
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) cmsStore.setState(() => JSON.parse(raw))
  } catch {
    // corrupted — start fresh
  }
  // Start persisting only after initial hydration to avoid clobbering storage
  // with the empty state before we've read it.
  cmsStore.subscribe(() => saveToStorage(cmsStore.state))
}

// --- Actions ---

export function addPendingEdit(file: string, id: string | undefined, updates: Record<string, unknown>) {
  const key = editKey(file, id)
  cmsStore.setState((prev) => {
    const existing = prev.pendingEdits[key]
    // If the item is still a pending 'add' (not yet published), keep it as an 'add'
    // and merge the updates into its data. Overwriting to 'update' would orphan the
    // item since it isn't present in deployed data for update to match against.
    const type = existing?.type === 'add' ? 'add' : 'update'
    return {
      ...prev,
      published: false,
      pendingEdits: {
        ...prev.pendingEdits,
        [key]: {
          file,
          id,
          type,
          data: { ...(existing?.data ?? {}), ...updates },
        },
      },
    }
  })
}

export function addPendingAdd(file: string, item: Record<string, unknown>) {
  const id = item.id as string
  const key = editKey(file, id)
  cmsStore.setState((prev) => ({
    ...prev,
    published: false,
    pendingEdits: {
      ...prev.pendingEdits,
      [key]: { file, id, type: 'add', data: item },
    },
  }))
}

export function addPendingReorder(file: string, orderedIds: string[]) {
  const key = `${file}:${REORDER_KEY_SUFFIX}`
  cmsStore.setState((prev) => ({
    ...prev,
    published: false,
    pendingEdits: {
      ...prev.pendingEdits,
      [key]: { file, type: 'reorder', data: { orderedIds } },
    },
  }))
}

export function discardPendingReorder(file: string) {
  const key = `${file}:${REORDER_KEY_SUFFIX}`
  cmsStore.setState((prev) => {
    const { [key]: _, ...rest } = prev.pendingEdits
    return { ...prev, pendingEdits: rest }
  })
}

export function addPendingDelete(file: string, id: string) {
  const key = editKey(file, id)
  cmsStore.setState((prev) => ({
    ...prev,
    published: false,
    pendingEdits: {
      ...prev.pendingEdits,
      [key]: { file, id, type: 'delete', data: {} },
    },
  }))
}

export function addPendingImage(path: string, dataUrl: string) {
  cmsStore.setState((prev) => ({
    ...prev,
    published: false,
    pendingImages: { ...prev.pendingImages, [path]: dataUrl },
  }))
}

export function markPublished() {
  cmsStore.setState((prev) => ({ ...prev, published: true }))
}

export function clearPendingEdits() {
  cmsStore.setState(() => ({ pendingEdits: {}, pendingImages: {}, published: false }))
  localStorage.removeItem(STORAGE_KEY)
}

export function discardPendingEdit(file: string, id?: string) {
  const key = editKey(file, id)
  cmsStore.setState((prev) => {
    const { [key]: _, ...rest } = prev.pendingEdits
    return { ...prev, pendingEdits: rest }
  })
}

export function discardPendingImage(path: string) {
  cmsStore.setState((prev) => {
    const { [path]: _, ...rest } = prev.pendingImages
    return { ...prev, pendingImages: rest }
  })
}

// --- Selectors ---

export function getPendingEditCount(state: CmsStoreState) {
  return Object.keys(state.pendingEdits).length + Object.keys(state.pendingImages).length
}

export function getUnpublishedCount(state: CmsStoreState) {
  if (state.published) return 0
  return getPendingEditCount(state)
}

export function getEditsForFile(state: CmsStoreState, file: string) {
  return Object.values(state.pendingEdits).filter((e) => e.file === file)
}

/**
 * Apply pending edits to deployed data for a given file.
 * Returns a new array/object with edits merged in.
 */
export function applyPendingEdits<T extends Record<string, unknown>>(
  deployed: T[],
  file: string,
  state: CmsStoreState,
): T[] {
  const edits = getEditsForFile(state, file)
  if (edits.length === 0) return deployed

  let result = [...deployed]

  for (const edit of edits) {
    if (edit.type === 'update' && edit.id) {
      const idx = result.findIndex((item) => (item as { id?: string }).id === edit.id)
      if (idx !== -1) {
        result[idx] = { ...result[idx], ...edit.data } as T
      }
    } else if (edit.type === 'add') {
      // Only add if not already present
      const exists = result.some((item) => (item as { id?: string }).id === edit.id)
      if (!exists) {
        result.push(edit.data as T)
      }
    } else if (edit.type === 'delete' && edit.id) {
      result = result.filter((item) => (item as { id?: string }).id !== edit.id)
    } else if (edit.type === 'reorder') {
      const orderedIds = (edit.data?.orderedIds as string[] | undefined) ?? []
      if (orderedIds.length > 0) {
        const byId = new Map(
          result.map((item) => [(item as { id?: string }).id, item] as const),
        )
        const reordered: T[] = []
        for (const id of orderedIds) {
          const item = byId.get(id)
          if (item) {
            reordered.push(item)
            byId.delete(id)
          }
        }
        // Append any items missing from the ordered list (e.g. newly added items)
        for (const remaining of byId.values()) reordered.push(remaining)
        result = reordered
      }
    }
  }

  return result
}

export function applyPendingObjectEdits<T extends Record<string, unknown>>(
  deployed: T,
  file: string,
  state: CmsStoreState,
): T {
  const edits = getEditsForFile(state, file)
  if (edits.length === 0) return deployed

  let result = { ...deployed }
  for (const edit of edits) {
    if (edit.type === 'update') {
      result = { ...result, ...edit.data } as T
    }
  }
  return result
}
